"use client";

import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { useEffect, useState, type MouseEvent } from "react";

/**
 * The clinic's photos on the details view (H59,
 * docs/ux/screens/BusinessPublicProfileScreen.md elements 1 and 1a).
 *
 * The cover is drawn full width at 16:9 and the rest follow in a strip that
 * scrolls sideways. Clicking any of them opens it full screen; the arrow keys
 * and buttons step through, and Escape, the close button or a click outside the
 * photo closes it. A photo that fails to load keeps its tile's surface fill.
 */
export function PhotoGallery({ urls, name }: { urls: readonly string[]; name: string }) {
  const [open, setOpen] = useState<number | null>(null);

  useEffect(() => {
    if (open === null) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(null);
      if (event.key === "ArrowRight") setOpen((i) => (i === null ? i : (i + 1) % urls.length));
      if (event.key === "ArrowLeft")
        setOpen((i) => (i === null ? i : (i - 1 + urls.length) % urls.length));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, urls.length]);

  if (urls.length === 0) return null;
  const [cover, ...rest] = urls;

  return (
    <div className="mb-6">
      <button
        type="button"
        onClick={() => setOpen(0)}
        className="block w-full overflow-hidden rounded-xl bg-surface"
        aria-label={`Open the photos of ${name}`}
      >
        <Photo src={cover} className="aspect-video w-full object-cover" />
      </button>
      {rest.length > 0 && (
        <div className="mt-2 flex snap-x gap-2 overflow-x-auto pb-1">
          {rest.map((url, index) => (
            <button
              key={url}
              type="button"
              onClick={() => setOpen(index + 1)}
              className="h-20 w-28 shrink-0 snap-start overflow-hidden rounded-lg bg-surface"
              aria-label={`Open photo ${index + 2} of ${urls.length}`}
            >
              <Photo src={url} lazy className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {open !== null && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={`Photo ${open + 1} of ${urls.length}`}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => setOpen(null)}
        >
          <Photo
            src={urls[open]}
            className="max-h-full max-w-full rounded-lg object-contain"
            onClick={(event) => event.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Close"
            onClick={() => setOpen(null)}
            className="absolute right-4 top-4 rounded-full bg-white/15 p-2 text-white"
          >
            <X size={22} />
          </button>
          {urls.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous photo"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen((open - 1 + urls.length) % urls.length);
                }}
                className="absolute left-4 rounded-full bg-white/15 p-2 text-white"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                type="button"
                aria-label="Next photo"
                onClick={(event) => {
                  event.stopPropagation();
                  setOpen((open + 1) % urls.length);
                }}
                className="absolute right-4 rounded-full bg-white/15 p-2 text-white"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * A photo whose failed load leaves its tile's surface fill, not the browser's
 * broken-image outline (BusinessPublicProfileScreen.md, States).
 */
function Photo({
  src,
  className,
  lazy = false,
  onClick,
}: {
  src: string;
  className: string;
  lazy?: boolean;
  onClick?: (event: MouseEvent<HTMLImageElement>) => void;
}) {
  const [failed, setFailed] = useState(false);
  return (
    <img
      src={src}
      alt=""
      loading={lazy ? "lazy" : undefined}
      onError={() => setFailed(true)}
      onClick={onClick}
      className={`${className} ${failed ? "invisible" : ""}`}
    />
  );
}
